import React, { useEffect } from "react";

import BusinessIcon from "@mui/icons-material/Business";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Typography,
  Stack,
  Container,
  Grid,
  Breadcrumbs,
  Tab,
  Tabs,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate, useParams, useMatch } from "react-router";

import { ORGANIZATIONS } from "src/platform/constants/routes";
import { eventBus } from "src/platform/events/EventBus";
import { useActiveOrganization } from "src/platform/hooks/useActiveOrganization";
import { useTabParam } from "src/platform/hooks/useTabParam";
import { DotGrid } from "src/shell/components/DotGrid";
import { EmptyState } from "src/shell/components/EmptyState";
import ErrorCard from "src/shell/components/ErrorCard";
import { getContainerStyles } from "src/shell/styles/themeUtils";

import { useOrganizationLoader } from "../../hooks/useOrganizationLoader";
import MembersTab from "../MembersTab";

import AIOpsTab from "./AIOpsTab";
import DangerZone from "./DangerZone";
import FinOpsTab from "./FinOpsTab";
import GeneralSettingsCard from "./GeneralSettingsCard";
import OrganizationDetailsSkeleton from "./OrganizationDetailsSkeleton";
import OrganizationHeader from "./OrganizationHeader";
import OrgBillingTab from "./OrgBillingTab";
import { OrgMetricsPanel } from "./OrgMetricsPanel";
import OrgSecretsTab from "./OrgSecretsTab";
import PlatformIdentityProviderCard from "./PlatformIdentityProviderCard";
import TagSchemaTab from "./TagSchemaTab";
import TeamsTab from "./TeamsTab";

type OrgTab =
  | "overview"
  | "members"
  | "teams"
  | "secrets"
  | "billing"
  | "finops"
  | "aiops"
  | "tags"
  | "settings";

const OrganizationDetails = () => {
  const { orgAlias: alias } = useParams<{ orgAlias: string }>();
  const isEditMode = !!useMatch(ORGANIZATIONS.EDIT.path);
  const { tab, setTab } = useTabParam<OrgTab>("overview");
  const navigate = useNavigate();
  const {
    data: organization,
    loading,
    error,
    loadOrganization,
  } = useOrganizationLoader(eventBus);
  const { setActiveOrg } = useActiveOrganization();
  const theme = useTheme();

  useEffect(() => {
    if (alias) {
      void loadOrganization(alias);
      setActiveOrg(alias);
    }
  }, [alias, loadOrganization, setActiveOrg]);

  if (loading)
    return (
      <Container sx={getContainerStyles(theme)}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: theme.spacing(2) }}
        >
          <Typography color="inherit">Organization</Typography>
          <Typography color="text.primary">Loading…</Typography>
        </Breadcrumbs>
        <OrganizationDetailsSkeleton />
      </Container>
    );
  if (error)
    return (
      <ErrorCard
        error={error}
        title="Unable to load organization"
        onRetry={alias ? () => void loadOrganization(alias) : undefined}
      />
    );
  if (!organization)
    return (
      <Container sx={getContainerStyles(theme)}>
        <EmptyState
          icon={BusinessIcon}
          title="Organization not found"
          subtitle="This organization may have been deleted, or you may not have permission to view it."
          actionLabel="Back to organizations"
          onAction={() => void navigate(ORGANIZATIONS.LIST.path)}
        />
      </Container>
    );

  return (
    <Container sx={getContainerStyles(theme)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: theme.spacing(2) }}
      >
        <Typography color="inherit">Organization</Typography>
        <Typography color="text.primary">
          {organization?.name || "Loading..."}
        </Typography>
        {isEditMode && <Typography color="text.primary">Edit</Typography>}
      </Breadcrumbs>

      <DotGrid>
        <Stack spacing={4}>
          <OrganizationHeader
            organization={organization}
            onEditOrganization={() => setTab("settings")}
            onInviteMember={() => setTab("members")}
            onNavigateBilling={() => setTab("billing")}
          />

          <Tabs
            value={tab}
            onChange={(_e, newTab: OrgTab) => setTab(newTab)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              borderRadius: 1,
              minHeight: 40,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minHeight: 40,
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 1.5,
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <Tab label="Overview" value="overview" />
            <Tab label="Members" value="members" />
            <Tab label="Teams" value="teams" />
            <Tab label="Secrets" value="secrets" />
            <Tab label="Billing" value="billing" />
            <Tab label="FinOps" value="finops" />
            <Tab label="AIOps" value="aiops" />
            <Tab label="Tag Schema" value="tags" />
            <Tab label="Settings" value="settings" />
          </Tabs>

          {tab === "overview" && (
            <OrgMetricsPanel
              orgAlias={organization.alias}
              organization={organization}
            />
          )}

          {tab === "members" && <MembersTab orgAlias={organization.alias} />}

          {tab === "teams" && <TeamsTab orgAlias={organization.alias} />}

          {tab === "secrets" && <OrgSecretsTab orgAlias={organization.alias} />}

          {tab === "billing" && <OrgBillingTab orgAlias={organization.alias} />}

          {tab === "finops" && <FinOpsTab orgAlias={organization.alias} />}

          {tab === "aiops" && <AIOpsTab />}

          {tab === "tags" && <TagSchemaTab orgAlias={organization.alias} />}

          {tab === "settings" && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <GeneralSettingsCard
                  organization={organization}
                  onSaved={() => void loadOrganization(organization.alias)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DangerZone resourceName={organization.alias} />
              </Grid>
              <Grid item xs={12}>
                <PlatformIdentityProviderCard orgAlias={organization.alias} />
              </Grid>
            </Grid>
          )}
        </Stack>
      </DotGrid>
    </Container>
  );
};

export default OrganizationDetails;
